import { useContext, useMemo, useEffect, useRef } from 'react';
import { useStore } from 'zustand';
import { AccessCheckEvaluationContext } from '../contexts/AccessCheckEvaluationContext';
import { generateResourceKey } from '../utils/generateResourceKey';
/**
 * Hook for performing bulk access checks on multiple resources.
 * Registers multiple access check requests with the batch context and returns
 * their results along with a combined loading state.
 *
 * @param resourcesToCheck - Array of resources and actions to check access for
 * @returns Object containing results map keyed by request key and combined loading state
 */
export function useAccessChecks(resourcesToCheck) {
  const context = useContext(AccessCheckEvaluationContext);

  // Handle missing context
  if (!context) {
    throw new Error('useAccessCheck must be used within AccessCheckProvider');
  }

  // Generate unique keys for each access check request in the format "type:id:action"
  const requestKeys = useMemo(() => {
    return resourcesToCheck.map(resourceInfo => generateResourceKey(resourceInfo.resource, resourceInfo.action));
  }, [resourcesToCheck]);

  // Create a stable string representation of keys for dependency tracking
  const requestKeysString = useMemo(() => requestKeys.join('|'), [requestKeys]);

  // Register all access check requests with the batch context
  useEffect(() => {
    resourcesToCheck.forEach(resourceInfo => {
      context.registerRequest(resourceInfo.resource, resourceInfo.action);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, requestKeysString]);

  // Use context store if available, otherwise use fallback
  const storeState = useStore(context === null || context === void 0 ? void 0 : context.store, state => state);
  const prevResultsRef = useRef(undefined);

  // Build a map of accessCheckEvals for all requested access checks
  // Uses shallow equality comparison to prevent unnecessary re-renders
  const accessCheckEvals = useMemo(() => {
    const resultMap = new Map();
    requestKeys.forEach(key => {
      const result = context.getAccessCheckEval(key);
      // If accessCheckEvals.get(key) is undefined, we skip adding it to the map, since its not a result yet
      if (result) {
        resultMap.set(key, result);
      }
    });

    // Perform shallow comparison with previous results to maintain referential stability
    // eslint-disable-next-line react-hooks/refs
    const prev = prevResultsRef.current;
    // eslint-disable-next-line react-hooks/refs
    if (prev && prev.size === resultMap.size) {
      let hasChanged = false;
      for (const [key, value] of resultMap.entries()) {
        // eslint-disable-next-line react-hooks/refs
        if (prev.get(key) !== value) {
          hasChanged = true;
          break;
        }
      }
      if (!hasChanged) {
        return prev;
      }
    }

    // eslint-disable-next-line react-hooks/refs
    prevResultsRef.current = resultMap;
    return resultMap;
    // I want to keep the check on accessCheckEvals here to make sure recalculation only happens when store changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeState.accessCheckEvals, requestKeys, context]);

  // Determine if any of the requested access checks are still loading
  const isLoading = useMemo(() => {
    return requestKeys.some(key => context.isLoading(key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKeys, storeState.loading, storeState.pendingRequests]);

  // eslint-disable-next-line react-hooks/refs
  return {
    // eslint-disable-next-line react-hooks/refs
    accessCheckEvals,
    isLoading
  };
}